/**
 * 国际跳棋游戏逻辑
 * 严格按照国际正式比赛规则实现
 * 10×10百格跳棋：斜向移动、强制吃子、成王升级
 */

import { Player, Position, GameMode, Difficulty, CheckersPiece, CheckersPieceType, CheckersMove, CheckersGameState } from '../types/game';

// ==================== 基础工具函数 ====================

/**
 * 检查位置是否在棋盘内
 */
export const isValidPosition = (position: Position): boolean => {
  return position.row >= 0 && position.row < 10 && position.col >= 0 && position.col < 10;
};

/**
 * 获取普通棋子的移动方向
 */
export const getManDirections = (player: Player): Position[] => {
  if (player === 'black') {
    // 黑棋向下移动（从上到下）
    return [
      { row: 1, col: -1 }, // 左下
      { row: 1, col: 1 },  // 右下
    ];
  } else {
    // 白棋向上移动（从下到上）
    return [
      { row: -1, col: -1 }, // 左上
      { row: -1, col: 1 },  // 右上
    ];
  }
};

/**
 * 获取王棋的移动方向（四个对角方向）
 */
export const getKingDirections = (): Position[] => {
  return [
    { row: -1, col: -1 }, // 左上
    { row: -1, col: 1 },  // 右上
    { row: 1, col: -1 },  // 左下
    { row: 1, col: 1 },   // 右下
  ];
};

// ==================== 棋盘操作 ====================

/**
 * 创建初始国际跳棋棋盘 (10×10)
 * 黑棋在上方（行0-3），白棋在下方（行6-9）
 */
export const createCheckersBoard = (): (CheckersPiece | null)[][] => {
  const board: (CheckersPiece | null)[][] = Array(10).fill(null).map(() => Array(10).fill(null));

  // 放置黑棋（上方，行0-3）
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 10; col++) {
      // 只在深色格子上放置棋子 (row + col 为奇数)
      if ((row + col) % 2 === 1) {
        board[row][col] = { player: 'black', type: 'man' };
      }
    }
  }

  // 放置白棋（下方，行6-9）
  for (let row = 6; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      // 只在深色格子上放置棋子
      if ((row + col) % 2 === 1) {
        board[row][col] = { player: 'white', type: 'man' };
      }
    }
  }

  return board;
};

/**
 * 创建初始游戏状态
 */
export const createInitialGameState = (
  gameMode: GameMode = 'pvp',
  difficulty?: Difficulty
): CheckersGameState => {
  const board = createCheckersBoard();

  return {
    board,
    currentPlayer: 'black', // 黑棋先行
    status: 'playing',
    winner: null,
    moves: [],
    energy: { black: 0, white: 0 },
    skills: { black: [], white: [] },
    gameHistory: [],
    gameMode,
    difficulty,
    isAIThinking: false,
    gameType: 'checkers',

    // 国际跳棋特定状态
    selectedPiece: undefined,
    possibleMoves: [],
    mustCapture: false,
    captureSequence: [],
    isCapturing: false,
    lastCapture: undefined,

    // 棋子计数
    blackPiecesCount: 20,
    whitePiecesCount: 20,
    blackKingsCount: 0,
    whiteKingsCount: 0,

    // 游戏统计
    totalMoves: 0,
    captureMoves: 0,
    promotionMoves: 0,
  };
};

// ==================== 移动验证 ====================

/**
 * 检查是否可以吃子
 */
export const canCapture = (
  board: (CheckersPiece | null)[][],
  from: Position,
  to: Position,
  player: Player
): Position | null => {
  // 计算跳跃方向和中间位置
  const rowDiff = to.row - from.row;
  const colDiff = to.col - from.col;

  // 必须是斜向跳跃（距离为2）
  if (Math.abs(rowDiff) !== 2 || Math.abs(colDiff) !== 2) {
    return null;
  }

  const midRow = from.row + rowDiff / 2;
  const midCol = from.col + colDiff / 2;

  // 中间位置必须有对手的棋子
  const midPiece = board[midRow][midCol];
  if (!midPiece || midPiece.player === player) {
    return null;
  }

  // 目标位置必须为空
  if (board[to.row][to.col] !== null) {
    return null;
  }

  return { row: midRow, col: midCol };
};

/**
 * 获取普通棋子的吃子移动
 * 修复：普通兵在吃子时可以向任意斜向移动（包括后退）
 */
export const getManCaptureMoves = (
  board: (CheckersPiece | null)[][],
  position: Position,
  player: Player
): CheckersMove[] => {
  const moves: CheckersMove[] = [];
  // 在吃子时，普通兵可以向所有四个斜向移动（包括后退）
  const allDirections = getKingDirections();

  // 检查所有四个方向的跳跃吃子
  for (const dir of allDirections) {
    const jumpTo = {
      row: position.row + dir.row * 2,
      col: position.col + dir.col * 2
    };

    if (isValidPosition(jumpTo)) {
      const captured = canCapture(board, position, jumpTo, player);
      if (captured) {
        moves.push({
          player,
          from: position,
          to: jumpTo,
          captures: [captured]
        });
      }
    }
  }

  return moves;
};

/**
 * 获取王棋的吃子移动
 * 加强：王棋可以跳过任意距离的棋子，并支持复杂的多重连续吃子
 */
export const getKingCaptureMoves = (
  board: (CheckersPiece | null)[][],
  position: Position,
  player: Player
): CheckersMove[] => {
  const moves: CheckersMove[] = [];
  const directions = getKingDirections();

  for (const dir of directions) {
    // 检查该方向上所有可能的跳跃吃子
    // 王棋可以在任意距离发现对手棋子并跳过
    for (let distance = 1; distance < 10; distance++) {
      const nextPos = {
        row: position.row + dir.row * distance,
        col: position.col + dir.col * distance
      };

      if (!isValidPosition(nextPos)) {
        break; // 超出棋盘范围
      }

      const midPiece = board[nextPos.row][nextPos.col];

      if (midPiece && midPiece.player !== player) {
        // 发现对手棋子，寻找可以跳跃到的位置
        for (let jumpDistance = distance + 1; jumpDistance < 10; jumpDistance++) {
          const jumpTo = {
            row: position.row + dir.row * jumpDistance,
            col: position.col + dir.col * jumpDistance
          };

          if (!isValidPosition(jumpTo)) {
            break; // 超出棋盘范围
          }

          const targetCell = board[jumpTo.row][jumpTo.col];

          if (targetCell === null) {
            // 找到空位，可以跳跃吃子
            moves.push({
              player,
              from: position,
              to: jumpTo,
              captures: [{ row: nextPos.row, col: nextPos.col }],
              isKingMove: true
            });
            break; // 找到最近的空位即可
          } else {
            // 路径被阻挡，停止这个方向的跳跃
            break;
          }
        }
      } else if (midPiece && midPiece.player === player) {
        // 遇到自己的棋子，停止这个方向的检查
        break;
      }
      // 如果是空格子，继续寻找更远的对手棋子
    }
  }

  return moves;
};

/**
 * 获取普通棋子的普通移动
 */
export const getManNormalMoves = (
  board: (CheckersPiece | null)[][],
  position: Position,
  player: Player
): CheckersMove[] => {
  const moves: CheckersMove[] = [];
  const directions = getManDirections(player);

  for (const dir of directions) {
    const newPos = {
      row: position.row + dir.row,
      col: position.col + dir.col
    };

    if (isValidPosition(newPos) && board[newPos.row][newPos.col] === null) {
      moves.push({
        player,
        from: position,
        to: newPos
      });
    }
  }

  return moves;
};

/**
 * 获取王棋的普通移动
 * 加强：王棋可以向任意斜线方向移动不限格数（类似国际象棋的后）
 */
export const getKingNormalMoves = (
  board: (CheckersPiece | null)[][],
  position: Position,
  player: Player
): CheckersMove[] => {
  const moves: CheckersMove[] = [];
  const directions = getKingDirections();

  for (const dir of directions) {
    // 王棋可以向任意距离移动，直到被阻挡
    for (let distance = 1; distance < 10; distance++) {
      const newPos = {
        row: position.row + dir.row * distance,
        col: position.col + dir.col * distance
      };

      if (!isValidPosition(newPos)) {
        break; // 超出棋盘范围
      }

      const targetCell = board[newPos.row][newPos.col];

      if (targetCell === null) {
        // 空格子，可以移动到这里，并继续检查更远的距离
        moves.push({
          player,
          from: position,
          to: newPos,
          isKingMove: true
        });
      } else {
        // 遇到棋子，停止这个方向的检查
        // 如果是自己的棋子，不能越过；如果是对手棋子，也不能在普通移动中越过
        break;
      }
    }
  }

  return moves;
};

/**
 * 获取单个棋子的所有可能移动
 */
export const getPieceMoves = (
  board: (CheckersPiece | null)[][],
  position: Position,
  player: Player,
  piece: CheckersPiece
): CheckersMove[] => {
  if (piece.type === 'man') {
    // 普通棋子
    const captureMoves = getManCaptureMoves(board, position, player);
    if (captureMoves.length > 0) {
      return captureMoves; // 如果有吃子移动，只能返回吃子移动
    }
    return getManNormalMoves(board, position, player);
  } else {
    // 王棋
    const captureMoves = getKingCaptureMoves(board, position, player);
    if (captureMoves.length > 0) {
      return captureMoves; // 如果有吃子移动，只能返回吃子移动
    }
    return getKingNormalMoves(board, position, player);
  }
};

/**
 * 获取玩家的所有吃子移动
 */
export const getAllCaptureMoves = (
  board: (CheckersPiece | null)[][],
  player: Player
): CheckersMove[] => {
  const allCaptureMoves: CheckersMove[] = [];

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const piece = board[row][col];
      if (piece && piece.player === player) {
        const position = { row, col };
        const captureMoves = piece.type === 'man'
          ? getManCaptureMoves(board, position, player)
          : getKingCaptureMoves(board, position, player);
        allCaptureMoves.push(...captureMoves);
      }
    }
  }

  return allCaptureMoves;
};

/**
 * 检查玩家是否必须吃子（强制吃子规则）
 */
export const mustCapture = (
  board: (CheckersPiece | null)[][],
  player: Player
): boolean => {
  return getAllCaptureMoves(board, player).length > 0;
};

/**
 * 获取玩家的所有可能移动（遵循强制吃子规则）
 */
export const getAllPossibleMoves = (
  board: (CheckersPiece | null)[][],
  player: Player
): CheckersMove[] => {
  // 首先检查是否有吃子移动
  const captureMoves = getAllCaptureMoves(board, player);
  if (captureMoves.length > 0) {
    return captureMoves; // 如果有吃子移动，只能返回吃子移动（强制吃子）
  }

  // 没有吃子移动，返回所有普通移动
  const allMoves: CheckersMove[] = [];

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const piece = board[row][col];
      if (piece && piece.player === player) {
        const position = { row, col };
        const normalMoves = piece.type === 'man'
          ? getManNormalMoves(board, position, player)
          : getKingNormalMoves(board, position, player);
        allMoves.push(...normalMoves);
      }
    }
  }

  return allMoves;
};

// ==================== 游戏操作 ====================

/**
 * 检查移动后是否应该升级为王棋
 */
export const shouldPromote = (
  piece: CheckersPiece,
  fromRow: number,
  toRow: number
): boolean => {
  if (piece.type !== 'man') {
    return false; // 已经是王棋
  }

  // 黑棋到达底部（行9）升级
  if (piece.player === 'black' && toRow === 9) {
    return true;
  }

  // 白棋到达顶部（行0）升级
  if (piece.player === 'white' && toRow === 0) {
    return true;
  }

  return false;
};

/**
 * 执行移动并返回新的游戏状态
 */
export const executeMove = (
  gameState: CheckersGameState,
  move: CheckersMove
): CheckersGameState => {
  const newBoard = gameState.board.map(row => [...row]);
  const piece = newBoard[move.from.row][move.from.col]!;

  // 移动棋子
  newBoard[move.to.row][move.to.col] = piece;
  newBoard[move.from.row][move.from.col] = null;

  // 处理吃子
  if (move.captures && move.captures.length > 0) {
    for (const capture of move.captures) {
      newBoard[capture.row][capture.col] = null;
    }
  }

  // 检查是否升级为王棋
  let isPromotion = false;
  if (shouldPromote(piece, move.from.row, move.to.row)) {
    newBoard[move.to.row][move.to.col] = {
      ...piece,
      type: 'king'
    };
    isPromotion = true;
  }

  // 重新计算棋子数量
  let blackPieces = 0, whitePieces = 0;
  let blackKings = 0, whiteKings = 0;

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const p = newBoard[row][col];
      if (p) {
        if (p.player === 'black') {
          blackPieces++;
          if (p.type === 'king') blackKings++;
        } else {
          whitePieces++;
          if (p.type === 'king') whiteKings++;
        }
      }
    }
  }

  // 创建新游戏状态
  const newGameState: CheckersGameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: gameState.currentPlayer === 'black' ? 'white' : 'black',
    moves: [...gameState.moves, move],
    gameHistory: [...gameState.gameHistory, gameState],

    // 更新棋子计数
    blackPiecesCount: blackPieces,
    whitePiecesCount: whitePieces,
    blackKingsCount: blackKings,
    whiteKingsCount: whiteKings,

    // 更新统计
    totalMoves: gameState.totalMoves + 1,
    captureMoves: gameState.captureMoves + (move.captures?.length || 0),
    promotionMoves: gameState.promotionMoves + (isPromotion ? 1 : 0),

    // 重置选择状态
    selectedPiece: undefined,
    possibleMoves: [],
    mustCapture: false,
    isCapturing: false,
    lastCapture: undefined,
  };

  return newGameState;
};

/**
 * 检查游戏是否结束
 */
export const checkGameEnd = (
  board: (CheckersPiece | null)[][],
  currentPlayer: Player
): { isEnd: boolean; winner: Player | null; reason: string } => {
  // 检查是否有棋子被完全吃掉
  let blackPieces = 0, whitePieces = 0;

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const piece = board[row][col];
      if (piece) {
        if (piece.player === 'black') blackPieces++;
        else whitePieces++;
      }
    }
  }

  if (blackPieces === 0) {
    return { isEnd: true, winner: 'white', reason: '黑方所有棋子被吃掉' };
  }

  if (whitePieces === 0) {
    return { isEnd: true, winner: 'black', reason: '白方所有棋子被吃掉' };
  }

  // 检查当前玩家是否有可用移动
  const possibleMoves = getAllPossibleMoves(board, currentPlayer);
  if (possibleMoves.length === 0) {
    const winner = currentPlayer === 'black' ? 'white' : 'black';
    return { isEnd: true, winner, reason: `${currentPlayer === 'black' ? '黑方' : '白方'}无法移动` };
  }

  return { isEnd: false, winner: null, reason: '' };
};

// ==================== 连续吃子规则 ====================

/**
 * 检查是否可以在指定位置继续吃子（用于连续吃子）
 */
export const canContinueCapture = (
  board: (CheckersPiece | null)[][],
  position: Position,
  player: Player,
  alreadyCaptured: Position[] = []
): CheckersMove[] => {
  const piece = board[position.row][position.col];
  if (!piece || piece.player !== player) {
    return [];
  }

  const captures = piece.type === 'man'
    ? getManCaptureMoves(board, position, player)
    : getKingCaptureMoves(board, position, player);

  // 过滤掉重复吃子的移动（不能吃同一个棋子两次）
  return captures.filter(capture => {
    return !alreadyCaptured.some(captured =>
      captured.row === capture.captures[0].row &&
      captured.col === capture.captures[0].col
    );
  });
};

/**
 * 获取完整的连续吃子路径
 */
export const getMultiCapturePaths = (
  board: (CheckersPiece | null)[][],
  position: Position,
  player: Player,
  path: CheckersMove[] = [],
  capturedPieces: Position[] = []
): CheckersMove[][] => {
  const piece = board[position.row][position.col];
  if (!piece || piece.player !== player) {
    return [];
  }

  const currentCaptures = canContinueCapture(board, position, player, capturedPieces);
  const paths: CheckersMove[][] = [];

  if (currentCaptures.length === 0) {
    // 没有更多吃子，返回当前路径
    return path.length > 0 ? [path] : [];
  }

  // 递归查找所有可能的连续吃子路径
  for (const capture of currentCaptures) {
    // 创建临时棋盘来模拟这个吃子移动
    const tempBoard = board.map(row => [...row]);
    tempBoard[capture.to.row][capture.to.col] = tempBoard[capture.from.row][capture.from.col];
    tempBoard[capture.from.row][capture.from.col] = null;

    // 移除被吃的棋子
    for (const captured of capture.captures) {
      tempBoard[captured.row][captured.col] = null;
    }

    // 继续查找下一步的吃子
    const newPath = [...path, capture];
    const newCapturedPieces = [...capturedPieces, ...capture.captures];
    const furtherPaths = getMultiCapturePaths(tempBoard, capture.to, player, newPath, newCapturedPieces);

    paths.push(...furtherPaths);
  }

  return paths;
};

// ==================== 游戏状态管理 ====================

/**
 * 选择棋子
 */
export const selectPiece = (
  gameState: CheckersGameState,
  position: Position
): CheckersGameState => {
  const board = gameState.board;
  const piece = board[position.row][position.col];

  if (!piece || piece.player !== gameState.currentPlayer) {
    return {
      ...gameState,
      selectedPiece: undefined,
      possibleMoves: []
    };
  }

  // 检查是否必须吃子
  const mustCap = mustCapture(board, gameState.currentPlayer);

  // 获取可能的移动
  let possibleMoves: CheckersMove[] = [];

  if (mustCap) {
    // 强制吃子：只显示吃子移动
    possibleMoves = piece.type === 'man'
      ? getManCaptureMoves(board, position, gameState.currentPlayer)
      : getKingCaptureMoves(board, position, gameState.currentPlayer);
  } else {
    // 可以普通移动
    possibleMoves = getPieceMoves(board, position, gameState.currentPlayer, piece);
  }

  return {
    ...gameState,
    selectedPiece: position,
    possibleMoves,
    mustCapture: mustCap
  };
};

/**
 * 取消选择棋子
 */
export const deselectPiece = (gameState: CheckersGameState): CheckersGameState => {
  return {
    ...gameState,
    selectedPiece: undefined,
    possibleMoves: []
  };
};

/**
 * 执行移动并检查连续吃子
 * 修复：完善连续吃子的实现，确保符合国际跳棋规则
 */
export const makeMove = (
  gameState: CheckersGameState,
  move: CheckersMove
): CheckersGameState => {
  // 执行移动
  const newGameState = executeMove(gameState, move);

  // 检查游戏是否结束
  const gameEnd = checkGameEnd(newGameState.board, newGameState.currentPlayer);
  if (gameEnd.isEnd) {
    return {
      ...newGameState,
      status: 'finished',
      winner: gameEnd.winner
    };
  }

  // 检查是否有连续吃子（只有在吃子的情况下才检查）
  if (move.captures && move.captures.length > 0) {
    // 从新位置检查是否还能继续吃子
    const piece = newGameState.board[move.to.row][move.to.col]!;

    // 重要：连续吃子时使用原玩家，而不是切换后的玩家
    const originalPlayer = gameState.currentPlayer;
    const furtherCaptures = piece.type === 'man'
      ? getManCaptureMoves(newGameState.board, move.to, originalPlayer)
      : getKingCaptureMoves(newGameState.board, move.to, originalPlayer);

    if (furtherCaptures.length > 0) {
      // 有连续吃子，保持当前玩家不切换
      return {
        ...newGameState,
        currentPlayer: originalPlayer, // 保持原玩家
        selectedPiece: move.to,
        possibleMoves: furtherCaptures,
        mustCapture: true,
        isCapturing: true,
        lastCapture: move.to,
        captureSequence: [...(gameState.captureSequence || []), move.to]
      };
    } else {
      // 没有更多连续吃子，结束吃子序列，切换玩家
      return {
        ...newGameState,
        isCapturing: false,
        lastCapture: undefined,
        captureSequence: []
      };
    }
  }

  // 普通移动（没有吃子），切换玩家
  return newGameState;
};

export default {
  // 基础函数
  isValidPosition,
  getManDirections,
  getKingDirections,

  // 棋盘操作
  createCheckersBoard,
  createInitialGameState,

  // 移动验证
  canCapture,
  getManCaptureMoves,
  getKingCaptureMoves,
  getManNormalMoves,
  getKingNormalMoves,
  getPieceMoves,
  getAllCaptureMoves,
  mustCapture,
  getAllPossibleMoves,
  shouldPromote,
  executeMove,
  checkGameEnd,

  // 连续吃子规则
  canContinueCapture,
  getMultiCapturePaths,

  // 游戏状态管理
  selectPiece,
  deselectPiece,
  makeMove
};